import React from 'react';
import { Box, Typography } from '@mui/material';
import { APICONSTANTS, PEDIGREE } from 'src/components/Constants';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import axios from 'src/axios';
import { SnackContext } from 'src/store/ContextStore';
import { isPersistedMemberId } from '../../pedigree/saveReconciliation';

function MakeProband({
  node,
  memberRecords,
  setMemberRecords,
  handleClose,
  setPedigreeState,
  userId,
  isSaving = false
}) {
  const [loading, setLoading] = React.useState(false);
  const { setSnack } = React.useContext(SnackContext);

  const memberRecord = (memberRecords || []).find(
    (m) => String(m.pid) === String(node.pid)
  );
  const memberId =
    memberRecord?._id || memberRecord?.uid || node._id || node.uid;
  // Persisted ⇔ a server-assigned _id exists (backfilled by the save
  // reconciliation). uid CANNOT stand in for this check: since born-stable
  // uids landed, a freshly-added member's uid is already 24-hex before it has
  // ever been persisted, so an id-shape test on uid would wave through
  // unsaved members (server would reject with a misleading
  // "does not belong to this family" 400).
  const persistedId = memberRecord?._id ?? node._id;

  const onConfirm = async () => {
    if (loading || isSaving) return;

    // The backend clears EVERY member's isProband flag before validating
    // deeply, so unsaved members must be stopped client-side with clear
    // guidance rather than reaching the server at all.
    if (!isPersistedMemberId(persistedId)) {
      setSnack({
        status: true,
        msg: 'Save the pedigree first, then try again',
        severity: 'warning'
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        APICONSTANTS.promoteProband.replace(':user_id', userId),
        { memberId: String(memberId) }
      );

      // Functional DELTA update: only rows whose flag actually changes get a
      // new object. Anything merged into live state while the POST was in
      // flight (save-echo _id/upn/familyMemberId backfill, concurrent edits)
      // survives — a rebuild from the render-time snapshot would clobber it.
      setMemberRecords((prev) =>
        prev.map((p) => {
          const want = String(p.pid) === String(node.pid);
          return p.isProband === want ? p : { ...p, isProband: want };
        })
      );
      setPedigreeState((prev) => ({
        ...prev,
        save: PEDIGREE.saveStatus.IN_PROGRESS
      }));
      handleClose();
    } catch (err) {
      // promote-proband responds 400 with a distinct message for e.g. a
      // cross-family memberId — surface it when present.
      const backendMsg = err?.response?.data?.message;
      setSnack({
        status: true,
        msg:
          typeof backendMsg === 'string' && backendMsg
            ? backendMsg
            : 'Failed to update proband — please try again',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const displayName = [node?.firstName, node?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <CustomDialog
      open={true}
      handleClose={handleClose}
      showActions={true}
      saveBtnLabel={loading ? 'Updating…' : 'Confirm'}
      title={'Confirm Proband Change'}
      minHeight={210}
      onSave={onConfirm}
      allowBackgroundClose={handleClose}
      isParentHandlingLoading={true}
      isParentLoading={loading || isSaving}
    >
      <Box>
        <Typography
          sx={{
            fontWeight: 'bold'
          }}
        >
          {`Set this person as the proband for this family?`}
        </Typography>
        <Box
          sx={{
            py: 2
          }}
        >
          <Typography>
            {`Person: ${displayName || `UPN ${node?.upn || node?.pid || ''}`}`}
          </Typography>
        </Box>
        <Typography variant="subtitle2">
          {
            'This will mark the selected person as the active proband for this family.'
          }
        </Typography>
      </Box>
    </CustomDialog>
  );
}

export default MakeProband;
